import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { Property, PropertyStatus, UpdatePropertyRequest, SearchMember } from '../types/api';
import { trackEvent } from '../services/analytics';
import { Button, Modal } from './index';
import { StatusSelect } from './StatusSelect';
import { RatingInput } from './RatingInput';
import { NotesEditor } from './NotesEditor';
import { CommentSection } from './CommentSection';
import { PhotoGallery } from './PhotoGallery';
import { PhotoUpload } from './PhotoUpload';
import { UpgradePrompt } from './UpgradePrompt';
import { useTravelTimes, invalidateTravelTimeCache } from '../hooks/useTravelTimes';
import { useTier } from '../hooks/useTier';
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight, ExternalLink, MapPin, MoreHorizontal, Navigation, Pencil, Phone, Share2, X } from 'lucide-react';
import { formatFurnished } from '../utils/format';

interface PropertyDetailProps {
  property: Property;
  searchId: string | null;
  members: SearchMember[];
  onBack: () => void;
  onUpdate: (id: string, data: UpdatePropertyRequest) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  readOnly?: boolean;
}

export function PropertyDetail({ property, searchId, members, onBack, onUpdate, onDelete, readOnly }: PropertyDetailProps) {
  const { t, i18n } = useTranslation();
  const { isReadOnly: tierReadOnly } = useTier();
  const [updating, setUpdating] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [newFeature, setNewFeature] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteCommentId, setDeleteCommentId] = useState<string | null>(null);
  const [editingAddress, setEditingAddress] = useState(false);
  const [addressDraft, setAddressDraft] = useState(property.address);
  const [editingRent, setEditingRent] = useState(false);
  const [rentDraft, setRentDraft] = useState(String(property.rent));
  const [editingContact, setEditingContact] = useState(false);
  const [contactNameDraft, setContactNameDraft] = useState(property.contactName || '');
  const [contactPhoneDraft, setContactPhoneDraft] = useState(property.contactPhone || '');
  const [shareCopied, setShareCopied] = useState(false);
  const [showOverflowMenu, setShowOverflowMenu] = useState(false);

  // Combine listing images + property photos for the hero carousel
  const allHeroImages = [
    ...property.images.map(url => ({ url, source: 'Listing' as const })),
    ...(property.photos || []).map(p => ({ url: p.url, source: p.addedBy })),
  ];

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}?search=${searchId}&property=${property.id}`;
    const shareText = `${property.address} — £${property.rent.toLocaleString()}${property.listingType === 'sale' ? '' : '/mo'}`;
    trackEvent('share_property', { property_id: property.id, address: property.address, site: property.site });
    if (navigator.share) {
      try { await navigator.share({ title: property.address, text: shareText, url: shareUrl }); } catch { /* cancelled */ }
    } else {
      try {
        await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2000);
      } catch { /* no clipboard */ }
    }
  };

  useEffect(() => {
    const modal = document.querySelector('.property-detail-modal');
    const panel = document.querySelector('.side-panel');
    if (modal) modal.scrollTop = 0;
    else if (panel) panel.scrollTop = 0;
    else window.scrollTo(0, 0);
  }, [property.id]);

  useEffect(() => {
    setAddressDraft(property.address);
    setEditingAddress(false);
    setRentDraft(String(property.rent));
    setEditingRent(false);
    setContactNameDraft(property.contactName || '');
    setContactPhoneDraft(property.contactPhone || '');
    setEditingContact(false);
    setCurrentImageIndex(0);
  }, [property.id, property.address, property.rent, property.contactName, property.contactPhone]);

  const { travelTimes, loading: travelTimesLoading, error: travelTimesError } = useTravelTimes(searchId, property.address);

  const handleStatusChange = async (status: PropertyStatus) => {
    setUpdating(true);
    try { await onUpdate(property.id, { status }); } finally { setUpdating(false); }
  };

  const handleExclude = async (reason: string) => {
    setUpdating(true);
    try { await onUpdate(property.id, { status: 'excluded', excludeReason: reason }); } finally { setUpdating(false); }
  };

  const handleUserRatingChange = async (rating: number) => {
    setUpdating(true);
    try { await onUpdate(property.id, { userRating: rating }); } finally { setUpdating(false); }
  };

  const handleOtherRatingChange = async (rating: number) => {
    setUpdating(true);
    try { await onUpdate(property.id, { otherRating: rating }); } finally { setUpdating(false); }
  };

  const handleNotesSave = async (notes: string) => { await onUpdate(property.id, { notes }); };

  const handleAddComment = async (author: string, text: string) => {
    await onUpdate(property.id, { addComment: { author, text } });
  };

  const handleDeleteComment = async (commentId: string) => { setDeleteCommentId(commentId); };

  const confirmDeleteComment = async () => {
    if (!deleteCommentId) return;
    const commentId = deleteCommentId;
    setDeleteCommentId(null);
    setUpdating(true);
    try { await onUpdate(property.id, { removeCommentId: commentId }); } finally { setUpdating(false); }
  };

  const handleAddPhoto = async (url: string, addedBy: string) => {
    await onUpdate(property.id, { addPhoto: { url, addedBy } });
  };

  const handleRemovePhoto = async (photoId: string) => {
    setUpdating(true);
    try { await onUpdate(property.id, { removePhotoId: photoId }); } finally { setUpdating(false); }
  };

  const toLocalDate = (iso: string) => {
    const d = new Date(iso);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const toLocalTime = (iso: string) => {
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  const [viewingDatePart, setViewingDatePart] = useState(() => property.viewingDate ? toLocalDate(property.viewingDate) : '');
  const [viewingTimePart, setViewingTimePart] = useState(() => property.viewingDate ? toLocalTime(property.viewingDate) : '');

  useEffect(() => {
    setViewingDatePart(property.viewingDate ? toLocalDate(property.viewingDate) : '');
    setViewingTimePart(property.viewingDate ? toLocalTime(property.viewingDate) : '');
  }, [property.viewingDate]);

  const handleViewingDateChange = async (datePart: string, timePart: string) => {
    setUpdating(true);
    try {
      const combined = datePart ? new Date(`${datePart}T${timePart || '12:00'}`).toISOString() : null;
      await onUpdate(property.id, { viewingDate: combined });
    } finally { setUpdating(false); }
  };

  const handleAddressSave = async () => {
    const trimmed = addressDraft.trim();
    if (!trimmed || trimmed === property.address) { setAddressDraft(property.address); setEditingAddress(false); return; }
    setUpdating(true);
    try { invalidateTravelTimeCache(property.address); await onUpdate(property.id, { address: trimmed }); setEditingAddress(false); } finally { setUpdating(false); }
  };

  const handleRentSave = async () => {
    const parsed = parseFloat(rentDraft);
    if (isNaN(parsed) || parsed < 0 || parsed === property.rent) { setRentDraft(String(property.rent)); setEditingRent(false); return; }
    setUpdating(true);
    try { await onUpdate(property.id, { rent: parsed }); setEditingRent(false); } finally { setUpdating(false); }
  };

  const handleContactSave = async () => {
    const name = contactNameDraft.trim();
    const phone = contactPhoneDraft.trim();
    if (name === (property.contactName || '') && phone === (property.contactPhone || '')) { setEditingContact(false); return; }
    setUpdating(true);
    try {
      await onUpdate(property.id, { contactName: name || undefined, contactPhone: phone || undefined });
      setEditingContact(false);
    } finally { setUpdating(false); }
  };

  const confirmDelete = async () => {
    setShowDeleteConfirm(false);
    setUpdating(true);
    try { await onDelete(property.id); } finally { setUpdating(false); }
  };

  const handleAddFeature = async () => {
    const text = newFeature.trim();
    if (!text || property.features.includes(text)) return;
    setUpdating(true);
    try { await onUpdate(property.id, { features: [...property.features, text] }); setNewFeature(''); } finally { setUpdating(false); }
  };

  const handleRemoveFeature = async (featureName: string) => {
    setUpdating(true);
    try { await onUpdate(property.id, { features: property.features.filter(f => f !== featureName) }); } finally { setUpdating(false); }
  };

  const nextImage = () => setCurrentImageIndex(i => (i + 1) % allHeroImages.length);
  const prevImage = () => setCurrentImageIndex(i => (i - 1 + allHeroImages.length) % allHeroImages.length);

  useEffect(() => {
    const imageCount = allHeroImages.length;
    if (imageCount <= 1) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') setCurrentImageIndex(i => (i + 1) % imageCount);
      if (e.key === 'ArrowLeft') setCurrentImageIndex(i => (i - 1 + imageCount) % imageCount);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [allHeroImages.length]);

  // Close overflow menu on outside click
  useEffect(() => {
    if (!showOverflowMenu) return;
    const handleClick = () => setShowOverflowMenu(false);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [showOverflowMenu]);

  // Refs for collapsible sections — set initial open state once, then let native
  // <details> handle toggling (avoids React re-render resetting open state / scroll-to-top)
  const travelTimesRef = useRef<HTMLDetailsElement>(null);
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const photosRef = useRef<HTMLDetailsElement>(null);
  const manageRef = useRef<HTMLDetailsElement>(null);

  useEffect(() => {
    // Only open ONE section — the name="property-sections" attribute enforces
    // mutual exclusivity, so setting multiple to open simultaneously confuses
    // the browser's internal state and causes an off-by-one click target bug.
    const defaultOpen = travelTimesRef;

    if (defaultOpen.current) {
      defaultOpen.current.open = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [property.id]);

  // Editable specs inline handlers
  const handleSpecChange = async (field: string, value: string | number | boolean) => {
    setUpdating(true);
    try { await onUpdate(property.id, { [field]: value } as UpdatePropertyRequest); } finally { setUpdating(false); }
  };

  return (
    <div className="property-detail">
      {/* Sticky header */}
      <header className="detail-header">
        <Button variant="ghost" onClick={onBack} size="sm">
          {i18n.dir() === 'rtl' ? <ArrowRight size={16} aria-hidden="true" style={{ marginInlineEnd: 4 }} /> : <ArrowLeft size={16} aria-hidden="true" style={{ marginInlineEnd: 4 }} />} {t('app.propertyDetail.back')}
        </Button>
        <span className="detail-header-address">{property.address}</span>
        <div className="detail-header-actions">
          <a
            href={property.url}
            target="_blank"
            rel="noopener noreferrer"
            className="view-listing-link"
            onClick={() => trackEvent('view_listing', { property_id: property.id, address: property.address, site: property.site, url: property.url })}
          >
            {t('app.propertyDetail.viewListing')}
          </a>
          <div className="detail-overflow-wrapper">
            <button className="detail-overflow-btn" onClick={(e) => { e.stopPropagation(); setShowOverflowMenu(v => !v); }} aria-label={t('app.propertyDetail.moreOptions')}>
              <MoreHorizontal size={18} />
            </button>
            {showOverflowMenu && (
              <div className="detail-overflow-menu">
                <button className="overflow-menu-item" onClick={handleShare}>
                  <Share2 size={14} /> {shareCopied ? t('common.copied') : t('app.propertyDetail.share')}
                </button>
                {!readOnly && (
                  <button className="overflow-menu-item overflow-menu-danger" onClick={() => { setShowOverflowMenu(false); setShowDeleteConfirm(true); }}>
                    <X size={14} /> {t('common.delete')}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      {property.locked && (
        <UpgradePrompt message="This property is read-only. Upgrade to unlock editing." />
      )}

      {/* Hero Photo Carousel */}
      {allHeroImages.length > 0 && (
        <div className="image-carousel">
          <img src={allHeroImages[currentImageIndex]?.url} alt={`${property.address} - ${currentImageIndex + 1}`} />
          <span className="carousel-counter">{currentImageIndex + 1} / {allHeroImages.length}</span>
          {allHeroImages.length > 1 && (
            <>
              <button className="carousel-btn prev" onClick={prevImage} aria-label={t('app.propertyDetail.previousImage')}><ChevronLeft size={24} aria-hidden="true" /></button>
              <button className="carousel-btn next" onClick={nextImage} aria-label={t('app.propertyDetail.nextImage')}><ChevronRight size={24} aria-hidden="true" /></button>
              <div className="carousel-dots">
                {allHeroImages.slice(0, 10).map((_, i) => (
                  <span key={i} className={`dot ${i === currentImageIndex ? 'active' : ''}`} onClick={() => setCurrentImageIndex(i)} />
                ))}
                {allHeroImages.length > 10 && <span className="dot-more">+{allHeroImages.length - 10}</span>}
              </div>
            </>
          )}
          {!readOnly && !tierReadOnly && (
            <PhotoUpload propertyId={property.id} onAddPhoto={handleAddPhoto} disabled={updating} />
          )}
        </div>
      )}

      {allHeroImages.length === 0 && !readOnly && !tierReadOnly && (
        <div className="no-images-placeholder">
          <PhotoUpload propertyId={property.id} onAddPhoto={handleAddPhoto} disabled={updating} />
        </div>
      )}

      {/* Price + Status Badge + Address */}
      <div className="detail-content">
        <div className="detail-hero-info">
          <div className="detail-price-row">
            {editingRent ? (
              <input
                type="number"
                className="detail-rent-input"
                value={rentDraft}
                onChange={e => setRentDraft(e.target.value)}
                onBlur={() => handleRentSave()}
                onKeyDown={e => { if (e.key === 'Enter') handleRentSave(); if (e.key === 'Escape') { setRentDraft(String(property.rent)); setEditingRent(false); } }}
                min={0}
                autoFocus
                disabled={updating}
              />
            ) : (
              <span className="detail-price-large" onClick={!readOnly ? () => { setRentDraft(String(property.rent)); setEditingRent(true); } : undefined}>
                £{property.rent.toLocaleString()}{property.listingType === 'sale' ? '' : t('app.propertyDetail.rentSuffix')}
                {!readOnly && <Pencil size={12} className="inline-edit-icon" aria-hidden="true" />}
              </span>
            )}
            {!readOnly ? (
              <StatusSelect value={property.status} onChange={handleStatusChange} onExclude={handleExclude} disabled={updating || readOnly} compact />
            ) : (
              <span className={`status-badge status-${property.status}`}>{property.status}</span>
            )}
          </div>
          {property.status === 'excluded' && property.excludeReason && (
            <div className="exclude-reason-badge">{t('app.propertyDetail.excludeReason', { reason: property.excludeReason })}</div>
          )}

          {editingAddress ? (
            <input
              className="detail-address-input"
              value={addressDraft}
              onChange={e => setAddressDraft(e.target.value)}
              onBlur={() => handleAddressSave()}
              onKeyDown={e => { if (e.key === 'Enter') handleAddressSave(); if (e.key === 'Escape') { setAddressDraft(property.address); setEditingAddress(false); } }}
              maxLength={500}
              autoFocus
              disabled={updating}
            />
          ) : (
            <h1 className="detail-address" onClick={!readOnly ? () => { setAddressDraft(property.address); setEditingAddress(true); } : undefined}>
              {property.address}
              {!readOnly && <Pencil size={12} className="inline-edit-icon" aria-hidden="true" />}
            </h1>
          )}

          {/* Inline specs */}
          <div className="detail-specs-inline">
            {property.bedrooms} {t('app.propertyCard.bed')} · {property.bathrooms} {t('app.propertyCard.bath')} · {property.propertyType.charAt(0).toUpperCase() + property.propertyType.slice(1)} · {formatFurnished(property.furnished)}
            {property.deposit != null && <> · {t('app.propertyDetail.deposit')}: £{property.deposit.toLocaleString()}</>}
          </div>

          {/* Ratings inline */}
          <div className={`detail-section ratings-section${members.length <= 1 ? ' ratings-single' : ''}`}>
            <RatingInput label={members[0]?.name ?? 'Member 1'} value={property.userRating || 0} onChange={handleUserRatingChange} disabled={updating || readOnly || tierReadOnly} />
            {members.length > 1 && (
              <RatingInput label={members[1]?.name ?? 'Member 2'} value={property.otherRating || 0} onChange={handleOtherRatingChange} disabled={updating || readOnly || tierReadOnly} />
            )}
          </div>
        </div>

        {/* Travel Times — collapsible, default open if locations exist */}
        <details className="detail-collapsible" name="property-sections" ref={travelTimesRef}>
          <summary className="detail-collapsible-summary">
            {t('app.propertyDetail.travelTimesSection')}
            {travelTimes.length > 0 && <span className="section-badge">{travelTimes.length}</span>}
          </summary>
          <div className="detail-collapsible-content">
            <div className="map-embed-container-inline">
              <iframe
                title={`Map of ${property.address}`}
                src={`https://maps.google.com/maps?q=${encodeURIComponent(property.address)}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                className="map-embed"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(property.address)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="map-open-link"
              >
                <MapPin size={14} aria-hidden="true" />
                {t('app.propertyDetail.openInMaps')}
              </a>
            </div>
            <div className="travel-times">
              {travelTimesLoading && (
                <div className="travel-time-row"><span className="travel-info">{t('app.propertyDetail.calculatingTravelTimes')}</span></div>
              )}
              {travelTimesError && (
                <div className="travel-time-row"><span className="travel-info travel-time-error">{t('app.propertyDetail.failedLoadTravelTimes')}</span></div>
              )}
              {travelTimes.map((tt, i) => {
                const directionsUrl = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(tt.locationAddress)}&destination=${encodeURIComponent(property.address)}`;
                return (
                  <a key={i} href={directionsUrl} target="_blank" rel="noopener noreferrer" className="travel-time-row travel-time-link">
                    <div className="travel-time-left">
                      <Navigation size={14} aria-hidden="true" className="travel-icon" />
                      <span className="location-name">{tt.locationName}</span>
                    </div>
                    <div className="travel-time-values">
                      {tt.walkingMinutes !== null && <span className="travel-chip">{'\u{1F6B6}'} {tt.walkingMinutes}m</span>}
                      {tt.transitMinutes !== null && <span className="travel-chip">{'\u{1F687}'} {tt.transitMinutes}m</span>}
                      {tt.distanceKm !== null && <span className="travel-chip">{tt.distanceKm} km</span>}
                      <ExternalLink size={12} aria-hidden="true" className="travel-link-icon" />
                    </div>
                  </a>
                );
              })}
              {!travelTimesLoading && !travelTimesError && travelTimes.length === 0 && (
                <p className="travel-info">{t('app.propertyDetail.noLocationsMessage')}</p>
              )}
            </div>
          </div>
        </details>

        {/* Details — collapsible, default open */}
        <details className="detail-collapsible" name="property-sections" ref={detailsRef}>
          <summary className="detail-collapsible-summary">{t('app.propertyDetail.detailsSection')}</summary>
          <div className="detail-collapsible-content">
            {/* Editable specs */}
            <div className="detail-specs-editable">
              <div className="spec-edit-row spec-edit-row-half">
                <div className="spec-edit-half">
                  <span className="spec-edit-label">{t('app.propertyDetail.bedrooms')}</span>
                  {!readOnly ? (
                    <select className="spec-inline-select" value={property.bedrooms}
                      onChange={e => handleSpecChange('bedrooms', parseInt(e.target.value, 10))} disabled={updating}>
                      {Array.from({ length: 11 }, (_, i) => (
                        <option key={i} value={i}>{i}</option>
                      ))}
                    </select>
                  ) : (
                    <span>{property.bedrooms}</span>
                  )}
                </div>
                <div className="spec-edit-half">
                  <span className="spec-edit-label">{t('app.propertyDetail.bathrooms')}</span>
                  {!readOnly ? (
                    <select className="spec-inline-select" value={property.bathrooms}
                      onChange={e => handleSpecChange('bathrooms', parseInt(e.target.value, 10))} disabled={updating}>
                      {Array.from({ length: 11 }, (_, i) => (
                        <option key={i} value={i}>{i}</option>
                      ))}
                    </select>
                  ) : (
                    <span>{property.bathrooms}</span>
                  )}
                </div>
              </div>
              <div className="spec-edit-row">
                <span className="spec-edit-label">{t('app.propertyDetail.type')}</span>
                {!readOnly ? (
                  <select className="spec-inline-select" value={property.propertyType}
                    onChange={e => handleSpecChange('propertyType', e.target.value)} disabled={updating}>
                    <option value="house">{t('app.propertyDetail.propertyType.house')}</option>
                    <option value="flat">{t('app.propertyDetail.propertyType.flat')}</option>
                    <option value="studio">{t('app.propertyDetail.propertyType.studio')}</option>
                    <option value="bungalow">{t('app.propertyDetail.propertyType.bungalow')}</option>
                    <option value="maisonette">{t('app.propertyDetail.propertyType.maisonette')}</option>
                    <option value="room">{t('app.propertyDetail.propertyType.room')}</option>
                    <option value="other">{t('app.propertyDetail.propertyType.other')}</option>
                  </select>
                ) : (
                  <span>{property.propertyType}</span>
                )}
              </div>
              <div className="spec-edit-row">
                <span className="spec-edit-label">{t('app.propertyDetail.furnished')}</span>
                {!readOnly ? (
                  <select className="spec-inline-select" value={property.furnished}
                    onChange={e => handleSpecChange('furnished', e.target.value)} disabled={updating}>
                    <option value="furnished">{t('app.propertyDetail.furnishedOptions.furnished')}</option>
                    <option value="unfurnished">{t('app.propertyDetail.furnishedOptions.unfurnished')}</option>
                    <option value="part-furnished">{t('app.propertyDetail.furnishedOptions.partFurnished')}</option>
                  </select>
                ) : (
                  <span>{formatFurnished(property.furnished)}</span>
                )}
              </div>
              {property.floorArea && (
                <div className="spec-edit-row">
                  <span className="spec-edit-label">{t('app.propertyDetail.floorArea')}</span>
                  <span>{property.floorArea} {t('app.propertyDetail.sqft')}</span>
                </div>
              )}
              {property.availableFrom && (
                <div className="spec-edit-row">
                  <span className="spec-edit-label">{t('app.propertyDetail.available')}</span>
                  <span>{new Date(property.availableFrom).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                </div>
              )}
              {property.deposit != null && (
                <div className="spec-edit-row">
                  <span className="spec-edit-label">{t('app.propertyDetail.deposit')}</span>
                  <span>£{property.deposit.toLocaleString()}</span>
                </div>
              )}
            </div>

            {/* Features — unified tag chips */}
            <div className="features-list-editable">
              {[
                { key: 'hasGarden', label: t('app.propertyDetail.features.garden'), active: property.hasGarden },
                { key: 'hasParking', label: t('app.propertyDetail.features.parking'), active: property.hasParking },
                { key: 'petFriendly', label: t('app.propertyDetail.features.petFriendly'), active: property.petFriendly || false },
                { key: 'billsIncluded', label: t('app.propertyDetail.features.billsIncluded'), active: property.billsIncluded },
              ].map(({ key, label, active }) => (
                !readOnly ? (
                  <button
                    key={key}
                    type="button"
                    className={`feature-tag feature-tag-toggle${active ? '' : ' feature-tag-inactive'}`}
                    onClick={() => handleSpecChange(key, !active)}
                    disabled={updating}
                  >
                    {label}
                  </button>
                ) : (
                  active && <span key={key} className="feature-tag">{label}</span>
                )
              ))}
              {property.features.map((feature, i) => (
                <span key={i} className="feature-tag">
                  {feature}
                  {!readOnly && (
                    <button className="feature-remove-btn" onClick={() => handleRemoveFeature(feature)} disabled={updating} aria-label={`Remove ${feature}`}>
                      <X size={14} aria-hidden="true" />
                    </button>
                  )}
                </span>
              ))}
            </div>
            {!readOnly && (
              <div className="feature-add-row">
                <input type="text" className="feature-add-input" placeholder={t('app.propertyDetail.addFeaturePlaceholder')} value={newFeature} onChange={e => setNewFeature(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAddFeature()} maxLength={50} disabled={updating} />
                <Button size="sm" onClick={handleAddFeature} disabled={updating || !newFeature.trim()}>{t('common.add')}</Button>
              </div>
            )}
          </div>
        </details>

        {/* Photos — collapsible, default open if photos exist */}
        {((property.photos && property.photos.length > 0) || !readOnly) && (
          <details className="detail-collapsible" name="property-sections" ref={photosRef}>
            <summary className="detail-collapsible-summary">
              {t('app.propertyDetail.photosSection')}
              {property.photos && property.photos.length > 0 && <span className="section-badge">{property.photos.length}</span>}
            </summary>
            <div className="detail-collapsible-content">
              <PhotoGallery
                listingImages={property.images}
                photos={property.photos || []}
                onRemovePhoto={!readOnly ? handleRemovePhoto : undefined}
              />
              {!readOnly && !tierReadOnly && (
                <PhotoUpload propertyId={property.id} onAddPhoto={handleAddPhoto} disabled={updating} />
              )}
            </div>
          </details>
        )}

        {/* Notes & Comments — collapsible, default collapsed */}
        <details className="detail-collapsible" name="property-sections">
          <summary className="detail-collapsible-summary">
            {t('app.propertyDetail.notesCommentsSection')}
            {property.comments.length > 0 && <span className="section-badge">{property.comments.length}</span>}
          </summary>
          <div className="detail-collapsible-content">
            <NotesEditor value={property.notes || ''} onSave={handleNotesSave} disabled={updating || readOnly} />
            <CommentSection
              comments={property.comments}
              members={members}
              onAddComment={handleAddComment}
              onDeleteComment={handleDeleteComment}
              disabled={updating || readOnly}
            />
          </div>
        </details>

        {/* Manage — collapsible, auto-expand if viewing date exists */}
        <details className="detail-collapsible" name="property-sections" ref={manageRef}>
          <summary className="detail-collapsible-summary">{t('app.propertyDetail.manageSection')}</summary>
          <div className="detail-collapsible-content">
            <div className="detail-section">
              <StatusSelect value={property.status} onChange={handleStatusChange} onExclude={handleExclude} disabled={updating || readOnly} />
              {property.status === 'excluded' && property.excludeReason && (
                <div className="exclude-reason-badge">{t('app.propertyDetail.excludeReason', { reason: property.excludeReason })}</div>
              )}
            </div>

            <div className="detail-section viewing-date">
              <label className="input-label">{t('app.propertyDetail.viewingDate')}</label>
              <div className="viewing-date-row">
                <input type="date" className="input viewing-date-input" value={viewingDatePart} onChange={e => { setViewingDatePart(e.target.value); handleViewingDateChange(e.target.value, viewingTimePart); }} disabled={updating || readOnly} />
                <input type="time" className="input viewing-time-input" value={viewingTimePart} onChange={e => { setViewingTimePart(e.target.value); if (viewingDatePart) handleViewingDateChange(viewingDatePart, e.target.value); }} disabled={updating || readOnly} />
                {property.viewingDate && !readOnly && (
                  <button className="btn btn-ghost btn-sm viewing-date-clear" onClick={() => { setViewingDatePart(''); setViewingTimePart(''); handleViewingDateChange('', ''); }} disabled={updating}>{t('app.clear')}</button>
                )}
              </div>
            </div>

            <div className="detail-section contact-section">
              <label className="input-label">{t('app.propertyDetail.contact')}</label>
              {editingContact ? (
                <div className="contact-edit-row">
                  <input className="contact-input" placeholder={t('app.propertyDetail.contactName')} value={contactNameDraft} onChange={e => setContactNameDraft(e.target.value)} maxLength={100} autoFocus disabled={updating} />
                  <input className="contact-input" placeholder={t('app.propertyDetail.contactPhone')} type="tel" value={contactPhoneDraft} onChange={e => setContactPhoneDraft(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleContactSave()} maxLength={30} disabled={updating} />
                  <Button size="sm" onClick={handleContactSave} disabled={updating}>{t('common.save')}</Button>
                  <Button variant="ghost" size="sm" onClick={() => { setContactNameDraft(property.contactName || ''); setContactPhoneDraft(property.contactPhone || ''); setEditingContact(false); }}>{t('common.cancel')}</Button>
                </div>
              ) : property.contactName || property.contactPhone ? (
                <div className="contact-display">
                  {property.contactName && <span className="contact-name">{property.contactName}</span>}
                  {property.contactPhone && (
                    <a href={`tel:${property.contactPhone}`} className="contact-phone"><Phone size={14} aria-hidden="true" />{property.contactPhone}</a>
                  )}
                  {!readOnly && (
                    <button className="address-edit-btn" onClick={() => setEditingContact(true)} aria-label={t('app.propertyDetail.editContact')}><Pencil size={12} aria-hidden="true" /></button>
                  )}
                </div>
              ) : !readOnly ? (
                <button className="contact-add-btn" onClick={() => setEditingContact(true)} disabled={updating}>{t('app.propertyDetail.addContact')}</button>
              ) : (
                <span className="contact-empty">{t('app.propertyDetail.noContactSaved')}</span>
              )}
            </div>
          </div>
        </details>

        {/* Delete at bottom */}
        {!readOnly && (
          <div className="detail-danger-zone">
            <Button variant="ghost" size="sm" onClick={() => setShowDeleteConfirm(true)} disabled={updating} style={{ color: 'var(--error)' }}>
              {t('app.propertyDetail.deleteProperty')}
            </Button>
          </div>
        )}
      </div>

      <Modal isOpen={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} title={t('app.propertyDetail.deleteProperty')}>
        <p style={{ marginBottom: '16px' }}>{t('app.propertyDetail.deleteConfirmation')}</p>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <Button variant="secondary" size="sm" onClick={() => setShowDeleteConfirm(false)}>{t('common.cancel')}</Button>
          <Button variant="primary" size="sm" onClick={confirmDelete} style={{ background: 'var(--error)' }}>{t('common.delete')}</Button>
        </div>
      </Modal>

      <Modal isOpen={deleteCommentId !== null} onClose={() => setDeleteCommentId(null)} title={t('app.commentSection.deleteLabel')}>
        <p style={{ marginBottom: '16px' }}>{t('app.propertyDetail.deleteCommentConfirmation')}</p>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <Button variant="secondary" size="sm" onClick={() => setDeleteCommentId(null)}>{t('common.cancel')}</Button>
          <Button variant="primary" size="sm" onClick={confirmDeleteComment} style={{ background: 'var(--error)' }}>{t('common.delete')}</Button>
        </div>
      </Modal>
    </div>
  );
}
